import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { components as RSComponents } from 'react-select';
import Creatable from 'react-select/creatable';
import { stockSelectStyles } from 'modules/CbamStock/components/stockSelectStyles';
import { useCgDialogPortalContainer } from 'modules/Common/ui/CgDialog';
import { withPortalOffset } from 'modules/Common/utils/overlayHost';
import { createCnGoodFilterOption } from 'modules/Common/utils/cnGoodFilterOption';
import { formatCnCodeFull } from 'modules/Common/utils/formatCnCode';
import { matchCnCodeOptions, parseCnCodeList } from 'modules/Common/utils/parseCnCodeList';

// Custom MultiValueLabel: renders the same content as the default but adds
// a native `title` so the user can hover a truncated pill to see the full
// label. Pairs with `text-overflow: ellipsis` styling on the label.
const TruncatingMultiValueLabel = (props) => (
  <RSComponents.MultiValueLabel
    {...props}
    innerProps={{
      ...props.innerProps,
      title: typeof props.data?.label === 'string' ? props.data.label : undefined,
    }}
  />
);

const multiSelectComponents = { MultiValueLabel: TruncatingMultiValueLabel };

const NO_SKIPPED_PASTE = { unmatched: [], otherGroup: [] };

// `stockSelectStyles` is tuned for single-select rows (fixed 36px height
// for visual consistency in stock tables). For multi-select, that hard
// height clips the pills as soon as a second value is added — so we
// compose an override that lets the control grow vertically and the value
// container wrap. `indicatorsContainer` is unset (alignItems: flex-start)
// so the dropdown caret + clear stay anchored to the top row instead of
// stretching with the wrapped pills.
const multiOverrides = {
  control: (base, state) => {
    const baseObj = stockSelectStyles().control(base, state);
    return {
      ...baseObj,
      height: 'auto',
      minHeight: '36px',
      alignItems: 'flex-start',
    };
  },
  valueContainer: (base) => {
    const baseObj = stockSelectStyles().valueContainer(base);
    return {
      ...baseObj,
      flexWrap: 'wrap',
      paddingTop: 4,
      paddingBottom: 4,
    };
  },
  indicatorsContainer: (base) => ({
    ...stockSelectStyles().indicatorsContainer(base),
    height: 'auto',
    alignSelf: 'flex-start',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#f1f5f9',
    borderRadius: 6,
    margin: '2px 4px 2px 0',
    // Cap individual pill width — long CN-good labels (`7806 12 13 -
    // Beverage can body stock, of …`) get truncated with ellipsis instead
    // of wrapping into multi-line pills. Hovering the pill still reveals
    // the full label via the native title attribute (set below).
    maxWidth: '220px',
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: '0.8125rem',
    color: '#1e293b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  multiValueRemove: (base) => ({
    ...base,
    cursor: 'pointer',
    color: '#64748b',
    ':hover': { backgroundColor: '#e2e8f0', color: '#0f172a' },
  }),
};

function buildStyles(hasError, isMulti) {
  const base = stockSelectStyles(hasError);
  if (!isMulti) return base;
  return { ...base, ...multiOverrides };
}

// react-select wrapper for searchable / large option lists. The styles match
// StockSupplierModal so the visual language is consistent.
//
// Two storage shapes are supported via `storeAs`:
//   'value'  (default) — form state holds the option's value (string), or an
//                        array of strings for isMulti. Use for new forms.
//   'object'           — form state holds the FULL option object (preserving
//                        any extra fields beyond value/label, e.g.
//                        aggregatedGoodsCategory), or array of objects for
//                        isMulti. Required by legacy `formGroups` configs
//                        whose downstream code (Zod schemas, fetchValue
//                        cascades) reads those extra fields. The legacy
//                        adapter and HookFormGroup pass storeAs="object"
//                        for FormControlTypes.DropdownSelect.
export default function SearchSelectField({
  field,
  fieldState,
  id,
  options = [],
  valueKey = 'value',
  labelKey = 'label',
  placeholder,
  disabled,
  isClearable = true,
  isMulti = false,
  storeAs = 'value',
  // When true, the user can type a free-form value and it gets added as
  // a new option (Creatable from react-select). Required by configs like
  // FuelFormConfig / RawFormConfig / IntermediateFormConfig / SubProcess
  // where operators add fuels / materials that aren't on the standard
  // list. Created entries round-trip through the same `storeAs` contract.
  creatable = false,
  // Multi-select only: pasting a list of CN codes (an Excel column, a comma
  // list) selects every one found in the options instead of searching for
  // the whole blob. Options must be keyed by CN code.
  pasteCnCodes = false,
  // With pasteCnCodes: an option field every selection must share (e.g.
  // aggregatedGoodsCategory). Pasted codes outside the group already
  // selected (or the first pasted one's) are reported, not added.
  pasteGroupKey,
  menuPortalTarget,
}) {
  const { t } = useTranslation('common');
  const resolvedPlaceholder = placeholder ?? t('common:filter.searchPlaceholder', 'Search...');
  const SelectComponent = creatable ? Creatable : Select;

  // Dialog-aware portaling: inside a CgDialog the menu must portal into the
  // dialog content node — react-remove-scroll wheel-blocks anything portaled
  // to <body>. Outside a dialog the context is null and body is correct.
  const dialogContainer = useCgDialogPortalContainer();
  const portalTarget =
    menuPortalTarget ?? dialogContainer ?? (typeof document !== 'undefined' ? document.body : null);

  // Control the input text so an in-progress search survives blur. react-select
  // clears the input on `input-blur` / `menu-close` by default, which wipes a
  // draft when the user switches tabs or clicks outside the dropdown without
  // picking an option. We keep the text in those cases and only reset it once a
  // value is actually selected (`set-value`). The draft is purely local — it
  // never touches form state — so nothing is "saved" until an option is chosen.
  const [inputValue, setInputValue] = useState('');
  const handleInputChange = (newValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(newValue);
    } else if (actionMeta.action === 'set-value') {
      setInputValue('');
    }
  };

  const normalized = useMemo(
    () =>
      options.map((o) => ({
        value: String(o[valueKey] ?? ''),
        label: o[labelKey] ?? o.name ?? String(o[valueKey] ?? ''),
        raw: o,
      })),
    [options, valueKey, labelKey],
  );

  // Resolve `field.value` (whatever shape) → react-select's selected option(s).
  // For storeAs='object' we accept either the full object or just the value;
  // we always render against the normalized options so the visible label
  // reflects the latest options list, even if the stored object is stale.
  //
  // When the stored value doesn't match any normalized option we
  // synthesize one from the value itself (object → reuse its fields,
  // string → use it as both value and label). Without this fallback,
  // freshly-typed creatable values disappear from the input the moment
  // form state updates because react-select-creatable does NOT add the
  // created entry to the options list — that's the consumer's job (v1
  // CgSelectDropdown.jsx:54-58 did it explicitly). Same fallback also
  // covers the case where async options haven't loaded yet but the form
  // already has a stored value (edit flow).
  const value = useMemo(() => {
    const v = field.value;
    if (v === undefined || v === null || v === '') return null;

    const valueOf = (entry) => (entry && typeof entry === 'object' ? entry[valueKey] : entry);
    const synth = (entry) =>
      entry && typeof entry === 'object'
        ? {
            value: String(entry[valueKey] ?? ''),
            label: entry[labelKey] ?? entry.name ?? String(entry[valueKey] ?? ''),
            raw: entry,
          }
        : { value: String(entry ?? ''), label: String(entry ?? ''), raw: undefined };

    if (isMulti) {
      const arr = Array.isArray(v) ? v : [];
      // Match in order of `arr` so the user-visible order mirrors form
      // state. Anything we can't find in `normalized` gets synthesized.
      return arr.map((entry) => {
        const wanted = String(valueOf(entry) ?? '');
        return normalized.find((o) => o.value === wanted) || synth(entry);
      });
    }

    const wanted = String(valueOf(v) ?? '');
    return normalized.find((o) => o.value === wanted) || synth(v);
  }, [field.value, normalized, isMulti, valueKey, labelKey]);

  // Codes from the last bulk paste that were not added, and why.
  const [skippedPaste, setSkippedPaste] = useState(NO_SKIPPED_PASTE);
  const handlePaste = (event) => {
    const codes = parseCnCodeList(event.clipboardData?.getData('text') ?? '');
    // One code stays a normal search, so the user can still pick from the
    // menu; with no options loaded yet there is nothing to match against.
    if (codes.length < 2 || normalized.length === 0) return;
    const current = value || [];
    const groupOf = (o) => o?.raw?.[pasteGroupKey];
    // Anchor on the selection's own group; if it has not hydrated its
    // group yet, don't guess — let the paste fall through to the search.
    const currentGroup = current.map(groupOf).find((g) => g !== undefined);
    if (pasteGroupKey && current.length > 0 && currentGroup === undefined) return;
    event.preventDefault();
    const { matched, unmatched } = matchCnCodeOptions(codes, normalized);
    const anchor = pasteGroupKey ? (currentGroup ?? groupOf(matched[0])) : undefined;
    const inGroup = (o) => !pasteGroupKey || groupOf(o) === anchor;
    const added = matched.filter((o) => inGroup(o) && !current.some((c) => c.value === o.value));
    if (added.length) field.onChange([...current, ...added].map(emit));
    setSkippedPaste({
      unmatched: unmatched.map(formatCnCodeFull),
      otherGroup: matched.filter((o) => !inGroup(o)).map((o) => o.value),
    });
  };

  // Map a react-select option back into the shape this form expects.
  const emit = (opt) => {
    if (!opt) return storeAs === 'object' ? null : '';
    return storeAs === 'object'
      ? (opt.raw ?? { [valueKey]: opt.value, [labelKey]: opt.label })
      : opt.value;
  };

  // For creatable, build a synthetic option from the user's free-form
  // input and emit it through the same path. In 'value' mode the form
  // state holds just the input string; in 'object' mode it holds an
  // option-shaped object so downstream code reading `.value`/`.label`
  // still works.
  const handleCreateOption = (input) => {
    const created = { value: String(input), label: String(input), raw: undefined };
    if (isMulti) {
      const next = [...(value || []), created];
      field.onChange(next.map(emit));
    } else {
      field.onChange(emit(created));
    }
  };

  const select = (
    <SelectComponent
      inputId={id}
      options={normalized}
      value={value}
      onChange={(opt) => {
        if (isMulti) {
          setSkippedPaste(NO_SKIPPED_PASTE);
          const list = (opt || []).map(emit);
          field.onChange(list);
        } else {
          field.onChange(emit(opt));
        }
      }}
      onCreateOption={creatable ? handleCreateOption : undefined}
      onBlur={field.onBlur}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      // Space/dot/dash-insensitive matching so "72201100", "7220.11.00" and
      // "7220-11-00" all match the "7220 11 00 - …" CN-good labels. Strictly a
      // superset of the default substring filter, so non-CN options are
      // unaffected.
      filterOption={createCnGoodFilterOption}
      placeholder={resolvedPlaceholder}
      isDisabled={disabled}
      isClearable={isClearable}
      isMulti={isMulti}
      components={isMulti ? multiSelectComponents : undefined}
      menuPortalTarget={portalTarget}
      // react-select positions a portaled menu from the control's viewport
      // rect; inside a dialog that lands offset by the dialog's own corner.
      styles={{
        ...buildStyles(!!fieldState?.error, isMulti),
        menuPortal: (base) => ({ ...withPortalOffset(base, portalTarget), zIndex: 20000 }),
      }}
    />
  );

  if (!(pasteCnCodes && isMulti)) return select;
  const notice = (text) => (
    <p
      className="mt-1 text-xs text-warningColor"
      role="status"
    >
      {text}
    </p>
  );
  return (
    <div onPaste={handlePaste}>
      {select}
      {skippedPaste.unmatched.length > 0 &&
        notice(
          t('common:filter.pasteUnmatched', {
            defaultValue: 'Not in the list, so not added: {{codes}}',
            codes: skippedPaste.unmatched.join(', '),
          }),
        )}
      {skippedPaste.otherGroup.length > 0 &&
        notice(
          t('common:filter.pasteOtherGroup', {
            defaultValue: 'A different category from the rest, so not added: {{codes}}',
            codes: skippedPaste.otherGroup.join(', '),
          }),
        )}
    </div>
  );
}
